/* eslint-disable @typescript-eslint/no-explicit-any */
import { getDataValue } from '@juvare/json-data-util';
import { getErrorMessage } from '@juvare/widget-ui/Utils';
import { WidgetHandler } from '../../WidgetHandler';
import { valueProviderCache } from '../../valueProviderCache';
import { callConfigFunction } from './callConfigFunction';
import { findValueProvider } from './findValueProvider';
import { renderForm } from './renderForm';
/**
 * Loads value options for the widget if not statically defined or if parameters have changed.
 *
 * @template T - Widget configuration type that supports value providers.
 * @param {WidgetProps<T>} props - Props object containing configuration, state, and data accessors.
 * @param {boolean} [dataIsMultipleValues] - Indicates whether data should be treated as a multi-select list.
 * @param {boolean} [dataAsObject] - Whether to represent multi-values as an object instead of an array.
 */
export function loadValuesIfNeeded(props, dataIsMultipleValues, dataAsObject) {
    var _a;
    if (needToLoadValues(props)) {
        if (!props.config.values && props.config.valueProvider) {
            const applyValues = (values) => {
                props.internalData.loading = false;
                prepareDataAfterValuesAreLoaded(props.getData(), values, dataIsMultipleValues, dataAsObject, props.config.allowInvalidValues, props.setData, props.update, props.config.valueEquals);
                props.internalData.values = values;
                delete props.internalData.error;
            };
            const loadValues = (valuesPromise) => {
                props.internalData.loading = true;
                delete props.internalData.values;
                delete props.internalData.error;
                props.update();
                valuesPromise
                    .then((values) => {
                    applyValues(values);
                    renderForm(props);
                })
                    .catch((error) => {
                    console.error('Failed to load values', error);
                    props.internalData.loading = false;
                    delete props.internalData.values;
                    props.internalData.error = getErrorMessage(error);
                    if (!props.config.allowInvalidValues && props.getData() != null) {
                        props.setData(undefined);
                    }
                    else {
                        props.update();
                    }
                    renderForm(props);
                });
            };
            let params;
            if (props.config.valueProvider.params != null) {
                params = props.config.valueProvider.params;
            }
            else if (props.config.valueProvider.paramsFrom) {
                params = (_a = new WidgetHandler(props).select(props.config.valueProvider.paramsFrom)) === null || _a === void 0 ? void 0 : _a.getData();
            }
            else if (props.config.valueProvider.paramsFromFormData) {
                params = getDataValue(props.rootData, props.config.valueProvider.paramsFromFormData);
            }
            else if (props.config.valueProvider.paramsFromFormParams) {
                params = getDataValue(props.rootParams.formParams, props.config.valueProvider.paramsFromFormParams);
            }
            else if (props.config.valueProvider.paramsFunction) {
                params = callConfigFunction(props.config.valueProvider.paramsFunction, 'valueProvider paramsFunction', 'handler', new WidgetHandler(props));
            }
            const valueProvider = props.config.valueProvider.type ? findValueProvider(props, props.config.valueProvider.type) : null;
            if (valueProvider) {
                const previousParams = props.internalData.previousParams;
                if (JSON.stringify(params) !== JSON.stringify(previousParams) || previousParams === undefined) {
                    props.internalData.previousParams = params;
                    const values = valueProviderCache.getCachedValue(valueProvider.type, params);
                    if (values) {
                        applyValues(values);
                        renderForm(props);
                    }
                    else {
                        loadValues(valueProviderCache.getValue(valueProvider, params));
                    }
                }
            }
            else if (props.config.valueProvider.custom) {
                loadValues(callConfigFunction(props.config.valueProvider.custom, 'valueProvider custom', 'handler', new WidgetHandler(props)));
            }
            else {
                console.error('Value provider not found, type: ' + props.config.valueProvider.type);
                props.internalData.loading = false;
                delete props.internalData.values;
                props.internalData.error = 'Value provider not found';
                if (!props.config.allowInvalidValues && props.getData()) {
                    props.setData(undefined);
                }
                else {
                    props.update();
                }
            }
        }
        else {
            props.internalData.loading = false;
            prepareDataAfterValuesAreLoaded(props.getData(), props.config.values, dataIsMultipleValues, dataAsObject, props.config.allowInvalidValues, props.setData, undefined, props.config.valueEquals);
            delete props.internalData.values;
            delete props.internalData.error;
            props.update();
        }
    }
}
/**
 * Validates and transforms the data against loaded values and updates the widget state if needed.
 *
 * @param {*} oldData - The current widget data.
 * @param {Value[] | undefined} values - Loaded list of valid values.
 * @param {boolean | undefined} dataIsMultipleValues - Whether the data represents multiple values.
 * @param {boolean | undefined} dataAsObject - Whether to store multi-values as object.
 * @param {boolean | undefined} allowInvalidValues - Whether invalid values should be preserved.
 * @param {(data: any) => void} setData - Function to update widget data.
 * @param {() => void} [update] - Optional UI update trigger if `setData` is not used.
 * @param {(value1?: any, value2?: any) => boolean} [valueEquals] - Optional equality function for values.
 */
export function prepareDataAfterValuesAreLoaded(oldData, values, dataIsMultipleValues, dataAsObject, allowInvalidValues, setData, update, valueEquals) {
    if (!values) {
        values = [];
    }
    let setDataCalled = false;
    if (dataIsMultipleValues) {
        oldData = convertDataObjectToArray(oldData);
        let newData = insertFixedValues(oldData, values);
        if (!allowInvalidValues) {
            newData = removeInvalidValues(newData, values, valueEquals);
        }
        if (!checkIfDataObjectsEqual(oldData, newData)) {
            // Calling setData will trigger widget render, so no need to call update later
            setData(dataAsObject ? convertDataArrayToObject(newData) : newData);
            setDataCalled = true;
        }
    }
    else if (!allowInvalidValues) {
        const validValues = values.map((value) => value.value);
        const isValidData = valueEquals ? validValues.some((value) => valueEquals === null || valueEquals === void 0 ? void 0 : valueEquals(value, oldData)) : undefined;
        if (!validValues.includes(oldData) && isValidData === undefined) {
            if (oldData != null) {
                // Calling setData will trigger widget render, so no need to call update later
                setData(undefined);
                setDataCalled = true;
            }
        }
        if (typeof isValidData === 'boolean' && !isValidData) {
            if (oldData != null) {
                // Calling setData will trigger widget render, so no need to call update later
                setData(undefined);
                setDataCalled = true;
            }
        }
    }
    if (!setDataCalled) {
        // Need to call update so that widget is rendered when values are loaded.
        // If setData was called, then it already does the update, so no need to call it again.
        // If update is not passed, then values are static and there is no need to render.
        update === null || update === void 0 ? void 0 : update();
    }
}
/**
 * Converts checkbox-style object data to an array of selected keys.
 *
 * @param {*} data - Object or array to convert.
 * @returns {any[]} Array of selected values.
 */
export function convertDataObjectToArray(data) {
    if (Array.isArray(data)) {
        return data;
    }
    else if (data != null && typeof data === 'object') {
        const array = [];
        for (const [key, value] of Object.entries(data)) {
            if (value === true) {
                array.push(key);
            }
        }
        return array;
    }
    else {
        return [];
    }
}
/**
 * Converts an array of values into a checkbox-style object with true flags.
 *
 * @param {string[]} data - Array of values.
 * @returns {*} Object representation with boolean flags.
 */
export function convertDataArrayToObject(data) {
    const object = {};
    for (const key of data) {
        object[key] = true;
    }
    return object;
}
/**
 * Compares two values (arrays or checkbox-style objects) for shallow equality.
 *
 * @param {*} oldData - Original data.
 * @param {*} newData - New data to compare with.
 * @returns {boolean} True if the contents are equal (ignoring order).
 */
export function checkIfDataObjectsEqual(oldData, newData) {
    const oldArray = convertDataObjectToArray(oldData);
    const newArray = convertDataObjectToArray(newData);
    if (oldArray.length != newArray.length) {
        return false;
    }
    for (let i = 0; i < oldArray.length; i++) {
        // order is not important
        if (!newArray.includes(oldArray[i])) {
            return false;
        }
    }
    return true;
}
/**
 * Ensures all fixed values from the list are included in the data array.
 *
 * @param {any[]} data - Current data.
 * @param {Value[]} values - Value definitions, some may be marked as fixed.
 * @returns {any[]} Updated data array with fixed values inserted.
 */
export function insertFixedValues(data, values) {
    const newData = [...data];
    for (const value of values) {
        if (value.fixed && !newData.includes(value.value)) {
            newData.push(value.value);
        }
    }
    return newData;
}
/**
 * Filters out values from the data array that are not found in the value list.
 *
 * @param {any[]} data - Current data.
 * @param {Value[]} values - Valid values to compare against.
 * @param {(value1?: any, value2?: any) => boolean} [valueEquals] - Optional equality function.
 * @returns {any[]} Filtered data array with only valid values.
 */
export function removeInvalidValues(data, values, valueEquals) {
    const validValues = values.map((value) => value.value);
    return data.filter((value) => {
        if (valueEquals) {
            return validValues.some((validValue) => {
                return valueEquals(validValue, value);
            });
        }
        return validValues.includes(value);
    });
}
function needToLoadValues(props) {
    var _a;
    let check;
    if (!props.config.values && props.config.valueProvider) {
        if (props.config.valueProvider.type) {
            let params = props.config.valueProvider.params;
            if (params == null) {
                if (props.config.valueProvider.paramsFrom) {
                    params = (_a = new WidgetHandler(props).select(props.config.valueProvider.paramsFrom)) === null || _a === void 0 ? void 0 : _a.getData();
                }
                else if (props.config.valueProvider.paramsFromFormData) {
                    params = getDataValue(props.rootData, props.config.valueProvider.paramsFromFormData);
                }
                else if (props.config.valueProvider.paramsFromFormParams) {
                    params = getDataValue(props.rootParams.formParams, props.config.valueProvider.paramsFromFormParams);
                }
                else if (props.config.valueProvider.paramsFunction) {
                    params = callConfigFunction(props.config.valueProvider.paramsFunction, 'valueProvider paramsFunction', 'handler', new WidgetHandler(props));
                }
            }
            check = JSON.stringify({ type: props.config.valueProvider.type, params });
        }
        else if (props.config.valueProvider.custom) {
            check = 'custom';
        }
        else {
            check = 'unknown';
        }
    }
    else {
        check = 'values';
    }
    if (props.internalData.valueProviderCheck !== check || props.internalData.loadValues) {
        props.internalData.valueProviderCheck = check;
        delete props.internalData.loadValues;
        return true;
    }
    else {
        return false;
    }
}
